/**
 * @author       grady@zumper.com (Grady Kuhnline)
 * @copyright    Copyright (c) 2020, Zumper
 * @description  MapStorage.ts
 */

import { getKey } from './helpers/getKey'
import { getStorageKey } from './helpers/getStorageKey'
import type { EnhancedStorage } from './types/EnhancedStorage'
import type { EnhancedStorageOptions } from './types/EnhancedStorageOptions'

export type MapStorageOptions = EnhancedStorageOptions

export class MapStorage<
  TValue = unknown,
  TMap extends Map<string, TValue> = Map<string, TValue>,
> implements EnhancedStorage
{
  protected map: TMap

  protected options: MapStorageOptions

  constructor(
    ...[options, map]: Map<string, TValue> extends TMap
      ? [options: MapStorageOptions, map?: TMap]
      : [options: MapStorageOptions, map: TMap]
  ) {
    this.options = { ...options }
    // @ts-expect-error `Map` isn't assignable to `TMap`
    // I think the constructor signature above makes this safe
    this.map = map ?? new Map<string, TValue>()
  }

  get length() {
    return this.map.size
  }

  clear() {
    this.map.clear()
  }

  getItem(key: string) {
    return this.map.get(getStorageKey(key, this.options)) ?? null
  }

  key(index: number) {
    if (index >= this.map.size) {
      return null
    }
    let currentIndex = 0
    for (const key of this.map.keys()) {
      if (currentIndex === index) {
        return getKey(key, this.options)
      }
      currentIndex++
    }
    return null
  }

  removeItem(key: string) {
    this.map.delete(getStorageKey(key, this.options))
  }

  setItem(key: string, value: TValue) {
    this.map.set(getStorageKey(key, this.options), value)
  }
}
